import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  EventEmitter,
  Input,
  input,
  OnInit,
  Output,
  signal,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/community-ui-accordion';
import '@maersk-global/community-ui-accordion-item';
import { FileInput } from './file-input/file-input.component';
import { FileSelectComponent } from './file-select/file-select.component';
import { AemsFile } from '../../models/aems-file.model';
import { FileViewerComponent } from './file-viewer/file-viewer.component';
import { BehaviorSubject } from 'rxjs';
import { FilePreviewComponent } from './file-preview/file-preview.component';
import { BlobService } from '../../services/blob.service';
import { BlobErrorType } from '../../models/blob-error.model';
import { ToastrService } from '../../services/toastr-service';
import { v4 } from 'uuid';
import { utils as aemsUtils } from '../../helper/utils';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AttachmentForm } from '../../form-model/attachment.form.model';
import { ApplicationtTextService } from '../../services/application-text.service';
import {
  DamageImage,
  InspectionDetail,
  InspectionDetailsWithLine,
  InspectionLineDetail,
  InspectionSummary,
} from '../../models/inpection-detail.model';
import { CommonModule } from '@angular/common';
import { forEach } from 'lodash-es';
import { ErrorComponent } from '../error/error.component';

@Component({
  selector: 'app-file-upload',
  imports: [FileInput, FileViewerComponent, FilePreviewComponent, FileSelectComponent, CommonModule, ErrorComponent],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class FileUploadComponent implements OnInit, OnChanges {
  @Input() parentForm: FormGroup<AttachmentForm> | undefined;
  @Input() readOnly = false;
  @Input() inputFiles: AemsFile[] = [];
  @Input() resetComponentTrigger: number = 0; // Add external reset trigger
  inspectionDetailsWithLine = input.required<InspectionDetailsWithLine | undefined>();
  inspectionDetailsWithLineAfterUserSelection: InspectionDetailsWithLine | undefined;
  @Output() inspectionDetailsWithLineAfterUserSelectionDoneEvent = new EventEmitter<InspectionDetailsWithLine>();
  inspectionSummaryList: InspectionSummary[] = [];
  lineDetail?: InspectionLineDetail;
  lastConfirmedInspectionState = signal<Map<string, boolean>>(new Map<string, boolean>());
  resetTrigger = signal<number>(0);
  type = input.required<'full' | 'compact'>();
  maxFiles = input<number>(5);
  labelForCompact = input<string>();
  shopCode = input<string>('');
  disabled = input<boolean>(false);
  workOrderMode = input<string>('');
  disableNext: boolean | null = null;
  disablePrevious: boolean | null = null;
  openFileViewer: boolean = false;
  openUploadDialog: boolean = false;
  private userConfirmedSelection: boolean = false;
  private editModeInitializedList: string[] = [];
  files: AemsFile[] = [];
  selectedInspectionFiles: AemsFile[] = [];
  selectedAndUploadedFiles: AemsFile[] = [];
  selectedFile!: AemsFile;
  navDisplayTextForFileViewer = '';
  currentEquipmentNumber: string = '';
  private previousResetTrigger: number = 0;
  reInitUserSelectionOnEquipmentChangeForBulkOrder: boolean = false;

  // Add validation errors signal for inspection images
  validationErrors = signal<string[]>([]);
  disableDoneForUpload: boolean = false;

  fileViewerContext: 'default' | 'excludingSelected' = 'excludingSelected';
  get currentViewerFiles(): AemsFile[] {
    return this.fileViewerContext === 'default' ? this.selectedAndUploadedFiles : this.files;
  }

  get attachmentCount(): number {
    const details = this.inspectionDetailsWithLineAfterUserSelection ?? this.inspectionDetailsWithLine();
    const reportCount = details?.attachReport ? this.inspectionSummaryList.filter((inspection) => inspection.report?.fileUrl).length : 0;
    const lineDetail = details?.lineDetail;
    const lineItemKey = lineDetail
      ? `${lineDetail.repairCode}-${lineDetail.damageCode}-${lineDetail.repairLocation}`
      : details?.equipmentNumber;
    const linkedImageCount =
      details?.inspectionDetails.reduce(
        (count, inspection) =>
          count +
          (inspection.damageImages?.filter(
            (image) => image.isLinked && (details.isBulkOrderCall || image.linkedLineItemIdentifier === lineItemKey),
          ).length ?? 0),
        0,
      ) ?? 0;
    return this.files.length + reportCount + linkedImageCount;
  }

  constructor(
    private uploadToBlobService: BlobService,
    private toastrService: ToastrService,
    private fb: FormBuilder,
    private appTextSvc: ApplicationtTextService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['resetComponentTrigger'] &&
      changes['resetComponentTrigger'].currentValue !== this.previousResetTrigger &&
      changes['resetComponentTrigger'].currentValue > 0
    ) {
      this.resetComponent();
      this.previousResetTrigger = changes['resetComponentTrigger'].currentValue;
    }
  }

  ngOnInit(): void {
    this.files = this.files.concat(this.inputFiles);
    this.parentForm?.addControl('files', this.fb.nonNullable.control<AemsFile[]>(this.inputFiles));
    this.editModeInitializedList = [];
    if (this.inspectionDetailsWithLine() === undefined) {
      this.selectedAndUploadedFiles = [...this.files];
    }
  }

  private updates = effect(() => {
    const inspectionDetailsWithLine = this.inspectionDetailsWithLine();

    if (inspectionDetailsWithLine?.isBulkOrderCall) {
      const newEquipmentNumber = inspectionDetailsWithLine?.equipmentNumber ?? '';
      if (this.currentEquipmentNumber && this.currentEquipmentNumber !== newEquipmentNumber) {
        this.files = [];
        this.selectedAndUploadedFiles = [];
        this.reInitUserSelectionOnEquipmentChangeForBulkOrder = true;
      }
      this.currentEquipmentNumber = newEquipmentNumber;
    }

    if (inspectionDetailsWithLine) {
      this.inspectionSummaryList = this.getInspectionFiles();
      if (inspectionDetailsWithLine.attachReport) {
        this.selectedInspectionFiles = this.inspectionSummaryList.map((inspection) => inspection.report).filter((x) => x.fileUrl);
      }

      let lineDetail = inspectionDetailsWithLine?.lineDetail;
      let repairCode = lineDetail?.repairCode ?? '';
      let damageCode = lineDetail?.damageCode ?? '';
      let repairLocation = lineDetail?.repairLocation ?? '';
      let lineItemKey: string = `${repairCode}-${damageCode}-${repairLocation}`;
      if (inspectionDetailsWithLine.isEditMode && lineDetail) {
        this.selectedInspectionFiles = [];
        forEach(this.inspectionDetailsWithLine()?.inspectionDetails, (detail) => {
          detail.damageImages.forEach((image) => {
            if (image.linkedLineItemIdentifier === lineItemKey) {
              this.selectedInspectionFiles.push(this.mapDamageImageToAemsFile(image));
            }
          });
        });
      }

      let equipmentNumber = inspectionDetailsWithLine.equipmentNumber ?? '';
      if (equipmentNumber) {
        forEach(this.inspectionDetailsWithLine()?.inspectionDetails, (detail) => {
          detail.damageImages.forEach((image) => {
            if (image.isLinked && (inspectionDetailsWithLine.isBulkOrderCall || image.linkedLineItemIdentifier === equipmentNumber)) {
              let aemsFile = this.mapDamageImageToAemsFile(image);
              aemsFile.linkFile = true;
              this.selectedInspectionFiles.push(aemsFile);
            }
          });
        });
        this.editModeInitializedList.push(equipmentNumber);
      }
      if (lineDetail) {
        let allDamageImagesExist = this.checkAllDamageImagesPresentInUserSelection();
        if (!allDamageImagesExist) {
          this.editModeInitializedList = this.editModeInitializedList.filter((x) => x !== lineItemKey);
        }
      }
      if (lineDetail && !this.editModeInitializedList.includes(lineItemKey)) {
        this.updateInspectionStateForFileSelectionList(this.inspectionDetailsWithLine()?.inspectionDetails ?? []);
        this.onFileLinkedChange(this.selectedInspectionFiles, lineDetail);
        this.inspectionDetailsWithLineAfterUserSelection = inspectionDetailsWithLine;
        this.editModeInitializedList.push(lineItemKey);
      }
      this.selectedAndUploadedFiles = [...this.files.filter((file) => file.uploadedToBlob), ...this.selectedInspectionFiles];
      this.parentForm?.controls.files.setValue(this.selectedAndUploadedFiles);
    }
  });

  private checkAllDamageImagesPresentInUserSelection() {
    let allDamageImagesExist = true;
    forEach(this.inspectionDetailsWithLine()?.inspectionDetails, (detail) => {
      detail.damageImages.forEach((image) => {
        const userSelectionDetail = this.inspectionDetailsWithLineAfterUserSelection?.inspectionDetails.find(
          (userDetail) => userDetail.inspectionId === detail.inspectionId,
        );

        if (userSelectionDetail) {
          const userSelectionImage = userSelectionDetail.damageImages.find(
            (userImage) => userImage.id === image.id || userImage.guid === image.guid,
          );

          if (!userSelectionImage) {
            allDamageImagesExist = false;
          }
        } else {
          allDamageImagesExist = false;
        }
      });
    });
    return allDamageImagesExist;
  }

  getInspectionFiles(): InspectionSummary[] {
    let inspectionSummaryList: InspectionSummary[] = [];
    this.lineDetail = this.inspectionDetailsWithLine()?.lineDetail;
    const details = this.inspectionDetailsWithLine()?.inspectionDetails;
    if (details && Array.isArray(details)) {
      details.forEach((inspectionDetail: InspectionDetail) => {
        let inspectionSummary: InspectionSummary = {
          id: inspectionDetail.inspectionId,
          createdOn: inspectionDetail.createdOn,
          createdBy: inspectionDetail.createdBy,
          imageCount: inspectionDetail.damageImages.length,
          files: [],
          report: {} as AemsFile,
        };
        if (inspectionDetail.damageImages && inspectionDetail.damageImages.length > 0) {
          let aemsFiles: AemsFile[] = [];
          inspectionDetail.damageImages.forEach((image: DamageImage) => {
            if (image.contentType && image.extension && image.path && image.id) {
              const file: AemsFile = this.mapDamageImageToAemsFile(image);
              aemsFiles.push(file);
            }
          });
          inspectionSummary.files = aemsFiles;
        }
        if (inspectionDetail.inspectionReportURL && inspectionDetail.inspectionId) {
          inspectionSummary.report = this.mapInspectionDetailReport(inspectionDetail);
        }
        inspectionSummaryList.push(inspectionSummary);
      });
    }
    return inspectionSummaryList;
  }
  mapInspectionDetailReport(inspectionDetail: InspectionDetail): AemsFile {
    let fileName = aemsUtils.getFileNameFromUrl(inspectionDetail.inspectionReportURL);
    let generatedName = aemsUtils.generateFileNameWithIdAndExtension(inspectionDetail.inspectionId.toString(), fileName);
    let eirDisplayName = `EIR - ${generatedName}`;
    return {
      fileUrl: inspectionDetail.inspectionReportURL,
      shopCode: this.shopCode(),
      uploadedToBlob: true,
      generatedName: inspectionDetail.guid ?? '',
      fileType: aemsUtils.fileType(fileName),
      displayName: eirDisplayName,
      isDownloadDisabled: false,
      linkFile: true,
      isDeleteDisabled: true,
      isLinkFileDisabled: true,
    };
  }

  private mapDamageImageToAemsFile(image: DamageImage): AemsFile {
    let generatedName = aemsUtils.generateFileNameWithIdAndExtension(image.id, image.name);
    let eirDisplayName = `EIR - ${generatedName}`;
    return {
      fileUrl: image.path,
      shopCode: this.shopCode(),
      uploadedToBlob: true,
      generatedName: image.guid ?? '',
      fileType: aemsUtils.fileType(image.name),
      displayName: eirDisplayName,
      isDownloadDisabled: false,
      linkFile: image.isLinked,
      isDeleteDisabled: false,
      isLinkFileDisabled: false,
    };
  }

  async onFileAttached(event: File[]) {
    this.files = this.files.concat(
      event.map((incomingFile) => {
        return {
          file: incomingFile,
          progress$: new BehaviorSubject<number>(0),
          fileUrl: URL.createObjectURL(incomingFile),
          uploadedToBlob: false,
          shopCode: this.shopCode(),
          generatedName: v4() + '.' + incomingFile.name.substring(incomingFile.name.lastIndexOf('.') + 1)?.toLowerCase(),
          fileType: incomingFile.type,
          displayName: incomingFile.name,
          isDownloadDisabled: true,
        };
      }),
    );
    await this.uploadToBlob(this.files.filter((file) => !file.uploadedToBlob));

    this.selectedAndUploadedFiles = [...this.files.filter((file) => file.uploadedToBlob), ...this.selectedInspectionFiles];
    this.parentForm?.controls.files.setValue(this.selectedAndUploadedFiles);
  }

  private async uploadToBlob(files: AemsFile[]): Promise<any> {
    if (files.length == 0) return;
    return this.uploadToBlobService
      .uploadFiles(files, this.shopCode())
      .then((data) => {
        files.forEach((file) => {
          if (file.file) {
            var uploadedFiles = this.files.find((f) => f.generatedName == file.generatedName);
            if (uploadedFiles) uploadedFiles.uploadedToBlob = true;
          }
        });
      })
      .catch((e: BlobErrorType) => {
        if (e == BlobErrorType.NO_SAS_URL) {
          this.toastrService.showToastr(this.appTextSvc.AttachmentServiceDown, 'error');
        }
      });
  }
  onFileViewerClosed() {
    // Reset the disable buttons
    this.disableNext = false;
    this.disablePrevious = false;
    this.openFileViewer = false;
  }
  onOpenAttachmentClicked() {
    this.openUploadDialog = true;
  }

  onThumbnailClicked(file: AemsFile, context: 'default' | 'excludingSelected') {
    this.fileViewerContext = context;
    const files = this.currentViewerFiles;
    this.selectedFile = file;
    this.openFileViewer = true;
    let currentIndex = files.indexOf(file);
    this.navDisplayTextForFileViewer = `${currentIndex + 1}/${files.length}`;
    if (currentIndex == 0) {
      this.disablePrevious = true;
    }
    if (currentIndex == files.length - 1) {
      this.disableNext = true;
    }
  }
  onMoreItemsClicked() {
    this.selectedFile = this.files[1];
    this.openFileViewer = true;
  }

  closeUploadDialog() {
    if (!this.userConfirmedSelection) {
      this.resetTrigger.set(Date.now());
    }
    this.openUploadDialog = false;
    this.userConfirmedSelection = false;
    this.validationErrors.set([]);
    this.disableDoneForUpload = false;
  }

  onDone(lineDetail: InspectionLineDetail | undefined, isHeaderImageDelete: boolean = false) {
    this.userConfirmedSelection = true;
    this.selectedInspectionFiles = [];

    this.inspectionSummaryList = this.getInspectionFiles();
    this.inspectionDetailsWithLineAfterUserSelection = this.inspectionDetailsWithLineAfterUserSelection ?? this.inspectionDetailsWithLine();
    if (this.inspectionDetailsWithLineAfterUserSelection?.attachReport) {
      this.selectedInspectionFiles.push(...this.inspectionSummaryList.map((inspection) => inspection.report));
    }

    let repairCode = lineDetail?.repairCode ?? '';
    let damageCode = lineDetail?.damageCode ?? '';
    let repairLocation = lineDetail?.repairLocation ?? '';
    let lineItemKey: string = `${repairCode}-${damageCode}-${repairLocation}`;

    let equipmentNumber = this.inspectionDetailsWithLine()?.equipmentNumber ?? '';
    let isBulkOrderCall = this.inspectionDetailsWithLine()?.isBulkOrderCall ?? false;

    forEach(this.inspectionDetailsWithLineAfterUserSelection?.inspectionDetails, (inspectionDetail) => {
      inspectionDetail.damageImages
        ?.filter(
          (x) =>
            x.isLinked &&
            ((isBulkOrderCall && this.inspectionDetailsWithLineAfterUserSelection?.equipmentNumber === equipmentNumber) ||
              x.linkedLineItemIdentifier == lineItemKey ||
              (isHeaderImageDelete && x.linkedLineItemIdentifier === equipmentNumber)),
        )
        .forEach((damageImage) => {
          this.selectedInspectionFiles.push(this.mapDamageImageToAemsFile(damageImage));
        });
    });

    this.updateInspectionStateForFileSelectionList(this.inspectionDetailsWithLineAfterUserSelection?.inspectionDetails ?? []);

    this.selectedAndUploadedFiles = [...this.files.filter((file) => file.uploadedToBlob), ...this.selectedInspectionFiles];
    this.parentForm?.controls.files.setValue(this.selectedAndUploadedFiles);
    this.inspectionDetailsWithLineAfterUserSelectionDoneEvent.emit(this.inspectionDetailsWithLineAfterUserSelection);
    this.openUploadDialog = false;
  }
  private updateInspectionStateForFileSelectionList(inspectionDetails: InspectionDetail[]) {
    const confirmedStateMap = new Map<string, boolean>();
    forEach(inspectionDetails, (inspectionDetail) => {
      inspectionDetail.damageImages?.forEach((damageImage) => {
        let generatedName = damageImage.guid ?? '';
        confirmedStateMap.set(generatedName, damageImage.isLinked ?? false);
      });
    });
    this.lastConfirmedInspectionState.set(confirmedStateMap);
  }

  onDeleteFile(file: AemsFile) {
    this.files = this.files.filter((f) => f.generatedName != file.generatedName);
    this.parentForm?.controls.files.setValue(this.files);
    if (file.linkFile) {
      this.UnlinkEir(file);
    }
    this.selectedAndUploadedFiles = [...this.files.filter((file) => file.uploadedToBlob), ...this.selectedInspectionFiles];
  }

  UnlinkEir(file: AemsFile) {
    let inspectionDetails = this.inspectionDetailsWithLine();

    const matchingImage = inspectionDetails?.inspectionDetails
      ?.flatMap((detail) => detail.damageImages)
      ?.find((image) => image.guid === file.generatedName);
    let isHeaderImageDelete = false;
    if (matchingImage) {
      file.linkFile = false;
      let lineItemKey: string = matchingImage.linkedLineItemIdentifier ?? '';
      let lineDetail: InspectionLineDetail | undefined = undefined;

      if (!inspectionDetails?.isBulkOrderCall) {
        if (lineItemKey.split('-').length === 3) {
          lineDetail = {
            repairCode: lineItemKey.split('-')[0] ?? '',
            damageCode: lineItemKey.split('-')[1] ?? '',
            repairLocation: lineItemKey.split('-')[2] ?? '',
            repairDescription: 'NA',
          };
        } else {
          isHeaderImageDelete = true;
        }
      }

      this.onFileLinkedChange([file], undefined, true);
      this.onDone(lineDetail, isHeaderImageDelete);
      this.resetTrigger.set(Date.now());
    }
  }

  onNextClicked() {
    const files = this.currentViewerFiles;
    const currentSelected = this.selectedFile;
    if (!currentSelected) return;

    let currentIndex = files.findIndex((f) => f.generatedName === currentSelected.generatedName);

    let nextIndex = currentIndex + 1;
    this.selectedFile = files[nextIndex];
    if (nextIndex == files.length - 1) {
      this.disableNext = true;
    }
    if (this.disablePrevious) {
      this.disablePrevious = null;
    }
    this.navDisplayTextForFileViewer = `${nextIndex + 1}/${files.length}`;
  }

  onPreviousClicked() {
    const files = this.currentViewerFiles;
    const currentSelected = this.selectedFile;
    if (!currentSelected) return;

    let currentIndex = files.findIndex((f) => f.generatedName === currentSelected.generatedName);
    let prevIndex = currentIndex - 1;
    this.selectedFile = files[prevIndex];
    if (prevIndex == 0) {
      this.disablePrevious = true;
    }
    if (this.disableNext) {
      this.disableNext = null;
    }
    this.navDisplayTextForFileViewer = `${prevIndex + 1}/${files.length}`;
  }

  onFileLinkedChange(inspectionFiles: AemsFile[], lineDetail: InspectionLineDetail | undefined, isDeleteCall: boolean = false) {
    this.inspectionDetailsWithLineAfterUserSelection = this.inspectionDetailsWithLineAfterUserSelection ?? this.inspectionDetailsWithLine();
    if (this.reInitUserSelectionOnEquipmentChangeForBulkOrder) {
      this.inspectionDetailsWithLineAfterUserSelection = this.inspectionDetailsWithLine();
      this.reInitUserSelectionOnEquipmentChangeForBulkOrder = false;
    }
    forEach(this.inspectionDetailsWithLineAfterUserSelection?.inspectionDetails, (inspectionDetail) => {
      inspectionDetail.damageImages?.forEach((damageImage) => {
        let generatedName = damageImage.guid;
        const linkedFile = inspectionFiles.find((file) => file.generatedName === generatedName);
        if (linkedFile) {
          damageImage.isLinked = linkedFile.linkFile ?? false;
          if (lineDetail) {
            let repairCode = lineDetail?.repairCode ?? '';
            let damageCode = lineDetail?.damageCode ?? '';
            let repairLocation = lineDetail?.repairLocation ?? '';
            let lineItemKey: string = `${repairCode}-${damageCode}-${repairLocation}`;
            damageImage.linkedLineItemIdentifier = damageImage.isLinked ? lineItemKey : '';
          }
          if (isDeleteCall) {
            damageImage.isLinked = false;
            damageImage.linkedLineItemIdentifier = '';
          }
        }
      });
    });

    this.validateLinkedImagesCount();
  }

  private validateLinkedImagesCount() {
    let isBulkOrderCall: boolean = this.inspectionDetailsWithLineAfterUserSelection?.isBulkOrderCall ?? false;
    const limit = this.maxFiles();
    const overLimit = this.attachmentCount > limit;
    if (overLimit) {
      this.validationErrors.set([
        isBulkOrderCall
          ? `Maximum ${limit} attachments can be added to a work order header.`
          : `Maximum ${limit} attachments can be added to a repair.`,
      ]);
      this.disableDoneForUpload = true;
    } else {
      this.validationErrors.set([]);
      this.disableDoneForUpload = false;
    }
  }

  resetComponent(): void {
    const inspectionDetailsWithLine = this.inspectionDetailsWithLine();
    let lineDetail = inspectionDetailsWithLine?.lineDetail;
    let repairCode = lineDetail?.repairCode ?? '';
    let damageCode = lineDetail?.damageCode ?? '';
    let repairLocation = lineDetail?.repairLocation ?? '';
    let lineItemKey: string = `${repairCode}-${damageCode}-${repairLocation}`;
    if (lineDetail) {
      this.selectedInspectionFiles = [];
      forEach(this.inspectionDetailsWithLine()?.inspectionDetails, (detail) => {
        detail.damageImages.forEach((image) => {
          if (image.linkedLineItemIdentifier === lineItemKey) {
            this.selectedInspectionFiles.push(this.mapDamageImageToAemsFile(image));
          }
        });
      });
    }
    this.onFileLinkedChange(this.selectedInspectionFiles, this.lineDetail);
    this.onDone(this.lineDetail, false);
    this.resetTrigger.set(Date.now());
  }
}
